/**
 * Generate Governance Map from Seeds — Auto-generates visual policy data
 * from the actual seed question data.
 *
 * ARCHITECTURE v4.0 — Replaces handmatig onderhouden VISUAL_GOVERNANCE_MAP.json
 *
 * This module analyzes all seed questions to derive:
 * - Which families require stimulus visuals (all questions have them)
 * - Which families require support visuals (any question has them)
 * - Default visual types per family (most common type)
 * - Support showWhen behavior (derived from seed data)
 *
 * The generated map has the same structure as the old JSON file,
 * ensuring backward compatibility with visualPolicyBuilder.ts.
 *
 * @see docs/architecture/VISUAL_AID_SYSTEM.md — Visual system reference
 */

import { QUESTIONS_BY_FAMILY, TAXONOMY } from '../../content';
import type { Question } from '../../content';

// ─── Types (same structure as old VISUAL_GOVERNANCE_MAP.json) ─────────────────

export interface GeneratedStimulusEntry {
  default: string | null;
  required: boolean;
  alternates?: string[];
}
export interface GeneratedSupportEntry {
  default: string | null;
  required: boolean;
  showWhen?: string;
  alternates?: string[];
}
export interface GeneratedFamilyEntry {
  module: string;
  skill: string;
  tier: number;
  levelCode: string;
  stimulus: GeneratedStimulusEntry;
  support: GeneratedSupportEntry;
}
export interface GeneratedVisualGovernanceMap {
  families: Record<string, GeneratedFamilyEntry>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Count occurrences of each type in an array and return the most common one.
 * Returns null if the array is empty.
 */
function mostCommonType(types: string[]): string | null {
  if (types.length === 0) return null;
  const counts = new Map<string, number>();
  for (const t of types) {
    counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  let maxCount = 0;
  let maxType: string | null = null;
  for (const [type, count] of counts) {
    if (count > maxCount) {
      maxCount = count;
      maxType = type;
    }
  }
  return maxType;
}

/**
 * Get all unique types from an array, excluding the default.
 */
function getAlternates(types: string[], defaultType: string | null): string[] {
  const unique = Array.from(new Set(types)).filter(t => t !== defaultType);
  return unique.length > 0 ? unique.sort() : [];
}

/**
 * Extract the most common showWhen value from support aids.
 */
function mostCommonShowWhen(questions: Question[]): string | undefined {
  const showWhens: string[] = [];
  for (const q of questions) {
    if (!q.supportAids) continue;
    for (const aid of q.supportAids) {
      const sw = (aid as {
        showWhen?: string;
      }).showWhen;
      if (sw) showWhens.push(sw);
    }
  }
  if (showWhens.length === 0) return undefined;
  return mostCommonType(showWhens) ?? undefined;
}

// ─── Generator ────────────────────────────────────────────────────────────────

/**
 * Generate the visual governance map from actual seed data.
 *
 * This replaces the handmatig maintained VISUAL_GOVERNANCE_MAP.json.
 * It analyzes all questions per family to derive visual requirements.
 *
 * Rules for derivation:
 * - stimulus.required = true if ALL questions in the family have stimulusAids
 *   (consensus: if every question provides a stimulus, the family requires it)
 * - stimulus.default = most common stimulus type across questions
 * - support.required = true if ANY question has supportAids
 *   (support is generally expected when present in seeds)
 * - support.default = most common support type across questions
 * - support.showWhen = most common showWhen value from support aids
 */
export function generateGovernanceMapFromSeeds(): GeneratedVisualGovernanceMap {
  const families: Record<string, GeneratedFamilyEntry> = {};
  for (const familyDef of TAXONOMY) {
    const questions = QUESTIONS_BY_FAMILY.get(familyDef.familyId);
    if (!questions || questions.length === 0) continue;
    const qs = questions;

    // ── Analyze stimulus aids ──────────────────────────────────────────────

    const allStimulusTypes: string[] = [];
    let questionsWithStimulus = 0;
    for (const q of qs) {
      if (Array.isArray(q.stimulusAids) && q.stimulusAids.length > 0) {
        questionsWithStimulus++;
        for (const aid of q.stimulusAids) {
          if (aid.type) allStimulusTypes.push(aid.type);
        }
      }
    }
    const stimulusDefault = mostCommonType(allStimulusTypes);
    // Required = ALL questions have stimulus (consensus-based)
    const stimulusRequired = questionsWithStimulus > 0 && questionsWithStimulus === qs.length;
    const stimulusAlternates = getAlternates(allStimulusTypes, stimulusDefault);

    // ── Analyze support aids ──────────────────────────────────────────────

    const allSupportTypes: string[] = [];
    let questionsWithSupport = 0;
    for (const q of qs) {
      if (Array.isArray(q.supportAids) && q.supportAids.length > 0) {
        questionsWithSupport++;
        for (const aid of q.supportAids) {
          if (aid.type) allSupportTypes.push(aid.type);
        }
      }
    }
    const supportDefault = mostCommonType(allSupportTypes);
    // Required = ANY question has support aids (support is expected when present)
    const supportRequired = questionsWithSupport > 0;
    const showWhen = mostCommonShowWhen(qs);
    const supportAlternates = getAlternates(allSupportTypes, supportDefault);

    // ── Build entry ───────────────────────────────────────────────────────

    const entry: GeneratedFamilyEntry = {
      module: familyDef.module,
      skill: familyDef.skill,
      tier: familyDef.tier,
      levelCode: familyDef.levelCode,
      stimulus: {
        default: stimulusDefault,
        required: stimulusRequired,
        ...(stimulusAlternates.length > 0 ? {
          alternates: stimulusAlternates
        } : {})
      },
      support: {
        default: supportDefault,
        required: supportRequired,
        ...(showWhen ? {
          showWhen
        } : {}),
        ...(supportAlternates.length > 0 ? {
          alternates: supportAlternates
        } : {})
      }
    };
    families[familyDef.familyId] = entry;
  }
  return {
    families
  };
}

// ─── Module-level Cache (lazy singleton) ──────────────────────────────────────

let _cachedMap: GeneratedVisualGovernanceMap | null = null;

/**
 * Get the generated governance map, using a module-level cache.
 * The map is built once from seed data and reused for all subsequent calls.
 * Call `invalidateGovernanceMapCache()` to force a rebuild.
 */
export function getGeneratedGovernanceMap(): GeneratedVisualGovernanceMap {
  if (!_cachedMap) {
    _cachedMap = generateGovernanceMapFromSeeds();
  }
  return _cachedMap;
}

/**
 * Invalidate the cached governance map, forcing a rebuild on next access.
 */
export function invalidateGovernanceMapCache(): void {
  _cachedMap = null;
}

/**
 * Get the number of families in the generated governance map.
 * Useful for diagnostics and validation.
 */
export function getGeneratedMapFamilyCount(): number {
  const map = getGeneratedGovernanceMap();
  return Object.keys(map.families).length;
}